<script module lang="ts">
	// The cash-point step (T32, UC-61, UC-62, #792): where this till's cash
	// goes, this till or one of its shop's cash points. It follows printers
	// in the onboarding (T27), and the Cash point row of Till settings (T26)
	// opens it as a Sheet whose Confirm sends the pick. One question, its
	// choices, and for another till the link test (LinkTest.svelte) that must
	// answer before Confirm turns on; then the store must have heard that
	// till's shift state, which a cash point on an older app never says
	// (decision 2026-10-22-onboarding-tests-the-link). Every refusal is the
	// server's or the store's, said in plain words; the pick and its gate are
	// src/till/cashPoint.svelte.ts's, and Till.svelte reads the cash point.
	import type { MessageKey } from '../i18n/messages';
	import type { CashPointGate } from '../till/cashPoint.svelte';

	/**
	 * What the step shows for its read: nothing for the first read's first
	 * 300 ms, then the compass; a failure, over the last answer if any; or
	 * the choices.
	 */
	export function cashPointReadState(cashPoint: {
		slow: boolean;
		failed: boolean;
		settled: boolean;
		body: unknown;
	}): 'waiting' | 'loading' | 'failed' | 'ready' {
		if (!cashPoint.settled && !cashPoint.body) return cashPoint.slow ? 'loading' : 'waiting';
		return cashPoint.failed ? 'failed' : 'ready';
	}

	/**
	 * The line under the choices while another till is picked: test its link
	 * first, its shift state being checked, or what Confirm does. An older app
	 * reads as a flag Band instead, and a till that shows none says nothing.
	 */
	export function cashPointLine(gate: CashPointGate): MessageKey | null {
		const keys: Record<CashPointGate, MessageKey | null> = {
			test: 'cashPointTestFirst',
			hearing: 'cashPointHearing',
			ready: 'cashPointReady',
			older_app: null,
		};
		return keys[gate];
	}
</script>

<script lang="ts">
	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Choice from '../components/Choice.svelte';
	import Loading from '../components/Loading.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { cashPointChoices, type TillCashPoint } from '../till/cashPoint.svelte';
	import type { TillMesh } from '../till/mesh.svelte';
	import LinkTest from './LinkTest.svelte';

	interface Props {
		language: Language;
		cashPoint: TillCashPoint;
		mesh: TillMesh;
		/** This till's own id. */
		selfId: string;
		/** The step's title; off in T26's Sheet, whose title says it. */
		heading?: boolean;
		/** A pass is on its way in the onboarding: presses do nothing. */
		disabled?: boolean;
	}
	let { language, cashPoint, mesh, selfId, heading = true, disabled = false }: Props = $props();

	// Read the mesh as the step shows: it says which tills run an older app,
	// which offer no link test.
	$effect(() => {
		const watched = mesh;
		untrack(() => void watched.read());
	});

	const readState = $derived(cashPointReadState(cashPoint));
	const choices = $derived(cashPointChoices(cashPoint.body, selfId));
	const picked = $derived(
		cashPoint.changed
			? choices.find((till) => till.tillId === cashPoint.picked && !till.self)
			: undefined,
	);
	const olderApp = $derived(picked !== undefined && cashPoint.olderApp);
	const line = $derived(picked && !olderApp ? cashPointLine(cashPoint.gate) : null);
	const kept = $derived(cashPoint.body?.cashPoint);
	const refusalName = $derived(picked?.name ?? '');
	const busy = $derived(disabled || cashPoint.saving);
</script>

<div class="step">
	{#if heading}<h3>{message(language, 'cashPointTitle')}</h3>{/if}
	{#if readState === 'loading'}
		<Loading
			label={message(language, 'tillSettingsLoading')}
			caption={message(language, 'tillSettingsLoading')}
		/>
	{/if}
	{#if readState === 'failed'}
		<Band
			tone="flag"
			alert
			testId="cash-point-read-error"
			action={cashPoint.retryable ? message(language, 'doneRetry') : undefined}
			actionTestId="cash-point-retry"
			actionBusy={cashPoint.loading}
			onAction={cashPoint.retryable ? () => void cashPoint.read() : undefined}
		>
			{message(language, 'cashPointReadFailed')}
		</Band>
	{/if}
	{#if cashPoint.body}
		{#if kept?.removed}<Band tone="flag" testId="cash-point-removed"
				>{fill(message(language, 'linkGone'), kept.name)}</Band
			>{/if}
		<Choice
			testId="cash-point-choices"
			title={message(language, 'cashPointQuestion')}
			stack
			options={choices.map((till) => ({
				value: till.tillId,
				label: till.self ? message(language, 'cashPointThisTill') : till.name,
				testId: till.self ? 'cash-point-choice-self' : `cash-point-choice-${till.tillId}`,
			}))}
			value={cashPoint.choice ?? ''}
			onChange={(tillId) => {
				if (!busy) cashPoint.pick(tillId as string, selfId);
			}}
		/>
		{#if choices.length === 1 && cashPoint.body.readAt}<p data-testid="cash-point-no-others">
				{message(language, 'cashPointNoOthers')}
			</p>{/if}
	{/if}
	{#if picked}
		{#if olderApp}
			<Band tone="flag" alert testId="cash-point-older-app"
				>{fill(message(language, 'cashPointOlderApp'), picked.name)}</Band
			>
		{:else}
			{@const tillId = picked.tillId}
			{#if line}<p role="status" data-testid="cash-point-line">
					{fill(message(language, line), picked.name)}
				</p>{/if}
			{#key tillId}
				<LinkTest
					{language}
					{tillId}
					name={picked.name}
					disabled={busy}
					onStart={() => cashPoint.retest(tillId)}
					onAnswer={(answer) => void cashPoint.tested(tillId, answer)}
				/>
			{/key}
		{/if}
	{/if}
	{#if cashPoint.refusal}
		<Band tone="flag" alert testId="cash-point-refusal"
			>{fill(message(language, cashPoint.refusal), refusalName)}</Band
		>
	{/if}
</div>

<style>
	.step {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}
	h3 {
		margin: 0;
		font-size: var(--text-h4);
	}
	p {
		margin: 0;
	}
</style>
